import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  StackSection,
  Stack,
  Card,
  Heading,
  DividerCard,
  Table,
  TableRow,
  TableCell,
  Inline,
  Text,
} from "@origin-digital/ods-core";
import {
  IconCheckCircle,
  IconPhone,
  IconThumbUp,
  IconHome,
} from "@origin-digital/ods-icons";
import content from "./content.json";
import { useAppContext } from "../../context/AppContext";
import { Routes } from "../../consts";
import {
  NavigationURIType,
  submitNavigationEvent,
} from "../../utils/analytics";

export const ThankYouScreen: React.FC = () => {
  const { context } = useAppContext();
  const history = useHistory();
  const todaysDate = new Date().toLocaleDateString();
  let displayAddress;
  const [navEventFired, setNavEventFired] = useState<boolean>(false);

  if (!context.lookupAddress) {
    history.push(Routes.INDEX);
  }

  if (!navEventFired) {
    const {
      location: { href },
    } = window;
    submitNavigationEvent(href, NavigationURIType.THANK_YOU);
    setNavEventFired(true);
  }

  if (context.lookupAddress?.isManualAddress) {
    displayAddress = `${context.lookupAddress?.qasAddress.unit ?? ""} ${
      context.lookupAddress?.qasAddress.streetNumber
    } ${context.lookupAddress?.qasAddress.streetName} ${
      context.lookupAddress?.qasAddress.city
    } ${context.lookupAddress?.qasAddress.state} ${
      context.lookupAddress?.qasAddress.postcode
    }`;
  } else {
    displayAddress = context.lookupAddress?.qasSearchResult?.label;
  }

  return (
    <StackSection data-id="thank-you-section" hideGutter>
      <Stack space="xsmall">
        <Card>
          <Stack space={["medium", "large"]} alignX="center">
            <IconCheckCircle
              color="positiveLight"
              size="medium"
              variant="solid"
            />
            <Heading variant="h3">{content.thankyou.heading}</Heading>
          </Stack>
        </Card>
        <DividerCard space="xlarge">
          <Card padding="small">
            <Stack space={["medium", "large"]}>
              <Text>{content.thankyou.description}</Text>
              <Text>{content.thankyou.body}</Text>
            </Stack>
          </Card>
          {displayAddress && (
            <Card padding="small">
              <Stack space="large">
                <Text variant="body">{displayAddress}</Text>

                <Stack space="xsmall">
                  <Text variant="body" weight="medium">
                    {content.thankyou.detailsHeading}
                  </Text>
                  <Table size="small" textVariant="body-small">
                    <TableRow>
                      <TableCell>
                        <Text tone="neutralLight" variant="body-small">
                          {content.thankyou.requestDateLabel}
                        </Text>
                      </TableCell>
                      <TableCell alignX="right">{todaysDate}</TableCell>
                    </TableRow>

                    {context.selectedOffer && (
                      <TableRow>
                        <TableCell>
                          <Text tone="neutralLight" variant="body-small">
                            {content.thankyou.offerDetailsLabel}
                          </Text>
                        </TableCell>
                        <TableCell
                          alignX="right"
                          data-id="thank-you-section-offer"
                        >
                          {context.selectedOffer.offerName}
                        </TableCell>
                      </TableRow>
                    )}
                  </Table>
                </Stack>
              </Stack>
            </Card>
          )}
        </DividerCard>
        <Card>
          <Stack space={["large", "xlarge"]}>
            <Heading variant="h3">{content.thankyou.nextStepsHeading}</Heading>

            <Stack space={["xsmall", "small"]}>
              <Inline space="xsmall">
                <IconPhone />
                <Heading variant="h4">{content.thankyou.callHeading}</Heading>
              </Inline>
              <Text>{content.thankyou.callDescription}</Text>
            </Stack>

            <Stack space={["xsmall", "small"]}>
              <Inline space="xsmall">
                <IconThumbUp />
                <Heading variant="h4">{content.thankyou.joinHeading}</Heading>
              </Inline>
              <Text>{content.thankyou.joinDescription}</Text>
            </Stack>
            <Stack space={["xsmall", "small"]}>
              <Inline space="xsmall">
                <IconHome />
                <Heading variant="h4">
                  {content.thankyou.installationHeading}
                </Heading>
              </Inline>
              <Text>{content.thankyou.installationDescription}</Text>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </StackSection>
  );
};
